import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug],
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
            .slice(0, 3)
        : [],
    [article],
  );

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-24 pb-20 text-center bg-background-50">
          <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
            <i className="ri-article-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除。</p>
          <Link
            to="/"
            className="whitespace-nowrap inline-block px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <>
      <ScrollToTop />
      <main>
        <Navbar />
        <article className="pt-20 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '分类浏览', to: '/category/guide' },
                { label: article.category, to: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="w-full h-[300px] md:h-[420px] overflow-hidden mt-4">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            <div className="max-w-3xl mx-auto py-10">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
                <span className="text-xs text-foreground-500">作者：{article.author}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-2 mb-10 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800
                  prose-h2:text-xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4 prose-h2:text-foreground-950
                  prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground-700
                  prose-ul:text-sm md:prose-ul:text-base prose-ul:leading-relaxed prose-ul:my-4
                  prose-li:my-1 prose-li:text-foreground-700
                  prose-strong:text-foreground-950"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-14 pt-8 border-t border-background-200/70">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="whitespace-nowrap text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-line"></i> 返回{article.category}
                  </Link>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-500">分享：</span>
                    <a
                      href="#"
                      className="w-8 h-8 flex items-center justify-center bg-background-100 rounded-full text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                      rel="nofollow"
                    >
                      <i className="ri-twitter-x-line text-sm"></i>
                    </a>
                    <a
                      href="#"
                      className="w-8 h-8 flex items-center justify-center bg-background-100 rounded-full text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                      rel="nofollow"
                    >
                      <i className="ri-linkedin-line text-sm"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="py-14 bg-background-100">
              <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
                <h2 className="text-xl font-bold text-foreground-950 mb-8 text-center">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer group"
                    >
                      <div className="w-full h-40 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2">{related.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>

        <Footer />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              author: {
                '@type': 'Person',
                name: article.author,
              },
              datePublished: article.date,
              image: article.image,
              publisher: {
                '@type': 'Organization',
                name: '18c.mic',
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://18c.mic',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '分类浏览',
                  item: 'https://18c.mic/category/guide',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.category,
                  item: `https://18c.mic/category/${article.categorySlug}`,
                },
                {
                  '@type': 'ListItem',
                  position: 4,
                  name: article.title,
                },
              ],
            }),
          }}
        />
      </main>
    </>
  );
}